// Helpers tipados del MÓDULO 4 (Juzgamiento). Centraliza las llamadas al backend
// para que las vistas no repitan rutas/DTOs. Base de la API y token JWT los resuelve
// `api` (lib/api.ts). Todo requiere Bearer. Los bloqueos procesales llegan como error
// 422 con { message, code }; `api()` propaga el `message` como Error.

import { api } from './api';

// ---- Enums locales (no vienen en /catalogos) ------------------------------
// SentidoFallo y TipoSancion no están en /catalogos; se usan constantes locales.
export type SentidoFallo =
  | 'ABSOLUTORIO'
  | 'SANCIONATORIO'
  | 'MIXTO'
  | 'TERMINACION_CAUSAL_LEGAL';

export const SENTIDO_FALLO: SentidoFallo[] = [
  'ABSOLUTORIO',
  'SANCIONATORIO',
  'MIXTO',
  'TERMINACION_CAUSAL_LEGAL',
];

export type TipoSancion =
  | 'DESTITUCION_INHABILIDAD_GENERAL'
  | 'SUSPENSION'
  | 'MULTA'
  | 'AMONESTACION_ESCRITA'
  | 'INHABILIDAD_ESPECIAL'
  | 'OTRA';

export const TIPO_SANCION: TipoSancion[] = [
  'DESTITUCION_INHABILIDAD_GENERAL',
  'SUSPENSION',
  'MULTA',
  'AMONESTACION_ESCRITA',
  'INHABILIDAD_ESPECIAL',
  'OTRA',
];

export type TipoJuicio = 'ORDINARIO' | 'VERBAL';
export type TipoVariacion = 'ERROR_CALIFICACION' | 'POR_JUZGAMIENTO';
export type DecisionNulidadJuicio = 'NIEGA' | 'PARCIAL' | 'TOTAL';
export type MedioAudiencia = 'PRESENCIAL' | 'VIRTUAL' | 'MIXTA';

export const MEDIO_AUDIENCIA: MedioAudiencia[] = ['PRESENCIAL', 'VIRTUAL', 'MIXTA'];

// Roles del módulo de juzgamiento (para RBAC en la UI).
export const ROLES_JUZGAMIENTO = ['FUNCIONARIO_INSTRUCCION', 'CONTRATISTA_INSTRUCCION', 'DIRECTOR_JUZGAMIENTO', 'ADMINISTRADOR'];
export const ROLES_DIRECTOR_JUZGAMIENTO = ['DIRECTOR_JUZGAMIENTO', 'ADMINISTRADOR'];
export const ROLES_FALLO = ['FUNCIONARIO_INSTRUCCION', 'CONTRATISTA_INSTRUCCION', 'DIRECTOR_JUZGAMIENTO', 'ADMINISTRADOR'];

export function puedeRepartirJuzgamiento(rol?: string): boolean {
  return !!rol && ROLES_DIRECTOR_JUZGAMIENTO.includes(rol);
}
export function puedeFallar(rol?: string): boolean {
  return !!rol && ROLES_FALLO.includes(rol);
}

// ---- Reparto / trámite (a nivel de expediente) ----------------------------

export function repartoJuzgamiento(
  id: string,
  body: { funcionarioId: string; terminoInterno?: string; observaciones?: string },
): Promise<unknown> {
  return api(`/expedientes/${id}/juzgamiento/reparto`, { method: 'POST', body });
}

export function devolverJuzgamiento(id: string, body: { motivo: string }): Promise<unknown> {
  return api(`/expedientes/${id}/juzgamiento/devolver`, { method: 'POST', body });
}

export function definirTramite(
  id: string,
  body: { tipoJuicio: TipoJuicio; fecha: string; fundamento: string; autoDefineKey?: string },
): Promise<unknown> {
  return api(`/expedientes/${id}/juzgamiento/definir-tramite`, { method: 'POST', body });
}

// ---- Juicio ordinario -----------------------------------------------------

export function ordinarioDisposicion(
  juicioId: string,
  body: { fechaNotificacion: string },
): Promise<unknown> {
  return api(`/juicio-ordinario/${juicioId}/disposicion`, { method: 'PATCH', body });
}

export function ordinarioDescargos(
  juicioId: string,
  body: {
    presentoDescargos: boolean;
    aportoPruebas: boolean;
    solicitoPruebas: boolean;
    presentoNulidad: boolean;
    descargosKey?: string;
  },
): Promise<unknown> {
  return api(`/juicio-ordinario/${juicioId}/descargos`, { method: 'PATCH', body });
}

export function ordinarioResolverNulidad(
  juicioId: string,
  body: { fecha: string; decision: DecisionNulidadJuicio; autoKey?: string },
): Promise<unknown> {
  return api(`/juicio-ordinario/${juicioId}/resolver-nulidad`, { method: 'POST', body });
}

export function ordinarioDecretarPruebas(
  juicioId: string,
  body: { fecha: string; autoPruebasKey?: string },
): Promise<unknown> {
  return api(`/juicio-ordinario/${juicioId}/decretar-pruebas`, { method: 'POST', body });
}

export function ordinarioTrasladoAlegatos(
  juicioId: string,
  body: { fecha: string },
): Promise<unknown> {
  return api(`/juicio-ordinario/${juicioId}/traslado-alegatos`, { method: 'POST', body });
}

// ---- Juicio verbal --------------------------------------------------------

export function verbalAutoVerbal(
  juicioId: string,
  body: {
    fecha: string;
    fechaAudiencia: string;
    horaAudiencia: string;
    medio: MedioAudiencia;
    linkAudiencia?: string;
  },
): Promise<unknown> {
  return api(`/juicio-verbal/${juicioId}/auto-verbal`, { method: 'POST', body });
}

export function verbalAudiencia(
  juicioId: string,
  body: {
    celebrada: boolean;
    actaKey?: string;
    motivoNoCelebracion?: string;
    nuevaFecha?: string;
    nuevaHora?: string;
  },
): Promise<unknown> {
  return api(`/juicio-verbal/${juicioId}/audiencia`, { method: 'POST', body });
}

export function verbalPracticaPruebas(
  juicioId: string,
  body: { fecha: string; practicadas: boolean },
): Promise<unknown> {
  return api(`/juicio-verbal/${juicioId}/practica-pruebas`, { method: 'POST', body });
}

export function verbalReanudarAlegatos(
  juicioId: string,
  body: { fecha: string },
): Promise<unknown> {
  return api(`/juicio-verbal/${juicioId}/reanudar-alegatos`, { method: 'POST', body });
}

export function verbalLecturaFallo(
  juicioId: string,
  body: { fecha: string; sentido: SentidoFallo; falloKey?: string },
): Promise<unknown> {
  return api(`/juicio-verbal/${juicioId}/lectura-fallo`, { method: 'POST', body });
}

// ---- Variación de cargos --------------------------------------------------

export function variacionCargos(
  id: string,
  body: {
    tipoJuicio: TipoJuicio;
    tipoVariacion: TipoVariacion;
    fecha: string;
    autoMotivadoKey?: string;
  },
): Promise<unknown> {
  return api(`/expedientes/${id}/variacion-cargos`, { method: 'POST', body });
}

// ---- Fallo primera instancia ----------------------------------------------

export function crearFallo(
  id: string,
  body: {
    fecha: string;
    sentido: SentidoFallo;
    tipoSancion?: TipoSancion;
    observaciones?: string;
    falloKey?: string;
  },
): Promise<{ id: string }> {
  return api<{ id: string }>(`/expedientes/${id}/fallos`, { method: 'POST', body });
}

export function notificarFallo(
  falloId: string,
  body: {
    notificacionPersonal: boolean;
    fecha: string;
    notificacionKey?: string;
    edictoKey?: string;
  },
): Promise<unknown> {
  return api(`/fallos/${falloId}/notificar`, { method: 'POST', body });
}

// ---- Recursos -------------------------------------------------------------

export function interponerRecurso(
  id: string,
  body: { tipoRecurso: 'APELACION'; forma?: string; fechaInterposicion: string; recursoKey?: string },
): Promise<{ id: string }> {
  return api<{ id: string }>(`/expedientes/${id}/recursos`, { method: 'POST', body });
}

export function concederRecurso(
  recursoId: string,
  body: {
    fecha: string;
    autoridadDestino: string;
    autoridadDestinoOtro?: string;
    autoConcedeKey?: string;
  },
): Promise<unknown> {
  return api(`/recursos/${recursoId}/conceder`, { method: 'POST', body });
}

// ---- Usuarios candidatos a reparto de juzgamiento -------------------------

export interface UsuarioLite {
  id: string;
  nombre: string;
  apellido: string;
  rol: string;
}

// Roles de juzgamiento a los que se puede repartir un expediente.
export const ROLES_FUNCIONARIO_JUZGAMIENTO = ['FUNCIONARIO_INSTRUCCION', 'CONTRATISTA_INSTRUCCION'];

/** Usuarios candidatos a reparto de juzgamiento (funcionarios/contratistas). Filtra en el cliente. */
export async function funcionariosJuzgamiento(): Promise<UsuarioLite[]> {
  const usuarios = await api<UsuarioLite[]>('/usuarios');
  return usuarios.filter((u) => ROLES_FUNCIONARIO_JUZGAMIENTO.includes(u.rol));
}

// ---- Helpers de detalle ---------------------------------------------------

/**
 * Extrae el id del registro de juicio (JuicioOrdinario/JuicioVerbal) desde el detalle
 * del expediente. El backend puede exponerlo con distintos nombres de clave; si no
 * está disponible directamente, se cae al id del registro `juzgamiento`.
 *
 * NOTA (documentado): las rutas de juicio (`/juicio-ordinario/:id`, `/juicio-verbal/:id`)
 * usan el id del registro JuicioOrdinario/JuicioVerbal. Si `GET /expedientes/:id` no lo
 * trae, se usa como respaldo el id de `juzgamiento` + su `tipo_juicio` para decidir qué
 * acciones mostrar.
 */
export function idJuicio(exp: unknown, tipo: TipoJuicio): string {
  const raw = exp as Record<string, unknown>;
  const claves =
    tipo === 'ORDINARIO'
      ? ['juicio_ordinario', 'juicioOrdinario', 'ordinario']
      : ['juicio_verbal', 'juicioVerbal', 'verbal'];

  // 1) Buscar el registro de juicio en la raíz del expediente.
  for (const k of claves) {
    const v = raw[k];
    if (v && typeof v === 'object' && 'id' in (v as object)) return String((v as { id: unknown }).id);
    if (typeof v === 'string' && v) return v;
  }

  // 2) Buscar dentro de `juzgamiento` (puede anidar el juicio o traer el id/tipo).
  const juz = raw['juzgamiento'] as Record<string, unknown> | undefined;
  if (juz && typeof juz === 'object') {
    for (const k of claves) {
      const v = juz[k];
      if (v && typeof v === 'object' && 'id' in (v as object)) return String((v as { id: unknown }).id);
      if (typeof v === 'string' && v) return v;
    }
    // Respaldo: usar el id del juzgamiento como referencia del juicio.
    if ('id' in juz) return String(juz['id']);
  }
  return '';
}

/** Tipo de juicio definido en el expediente (desde `juzgamiento.tipo_juicio`), si existe. */
export function tipoJuicioDe(exp: unknown): TipoJuicio | null {
  const raw = exp as Record<string, unknown>;
  const juz = raw['juzgamiento'] as Record<string, unknown> | undefined;
  const t = (juz?.['tipo_juicio'] ?? juz?.['tipoJuicio'] ?? raw['tipo_juicio']) as string | undefined;
  if (t === 'ORDINARIO' || t === 'VERBAL') return t;
  return null;
}

/** ¿El expediente tiene registro de juzgamiento (ya fue repartido a juzgamiento)? */
export function tieneJuzgamiento(exp: unknown): boolean {
  const raw = exp as Record<string, unknown>;
  return !!raw['juzgamiento'];
}

// ---- Sesión / RBAC de la UI -----------------------------------------------

export interface UsuarioSesion {
  id?: string;
  nombre?: string;
  rol?: string;
  [k: string]: unknown;
}

/** Lee el usuario en sesión desde localStorage ('usuario'). */
export function usuarioActual(): UsuarioSesion | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('usuario');
    return raw ? (JSON.parse(raw) as UsuarioSesion) : null;
  } catch {
    return null;
  }
}
